import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { 
  fetchInventoryOptimized, 
  fetchProductsOptimized, 
  fetchPendingOrdersOptimized, 
  fetchProductImages,
  recalculateInventoryOptimized,
  getInventoryStats,
  clearInventoryCache 
} from '@/lib/api/inventory';

export const useInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [productImages, setProductImages] = useState({});
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [progress, setProgress] = useState({ message: "", percentage: 0 });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  // Función de callback para el progreso del recálculo (throttle para evitar reflows)
  const lastProgressRef = useRef({ ts: 0, percentage: 0, message: '' });
  const rafRef = useRef(null);
  const handleProgress = useCallback((message, percentage) => {
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    const last = lastProgressRef.current;
    const elapsed = now - (last.ts || 0);
    const deltaPct = Math.abs((percentage || 0) - (last.percentage || 0));
    const messageChanged = message !== last.message;
    const shouldUpdate = (percentage === 100) || messageChanged || deltaPct >= 5 || elapsed >= 120;
    if (!shouldUpdate) return;
    lastProgressRef.current = { ts: now, percentage, message };
    if (rafRef.current && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(rafRef.current);
    }
    if (typeof requestAnimationFrame !== 'undefined') {
      rafRef.current = requestAnimationFrame(() => setProgress({ message, percentage }));
    } else {
      setProgress({ message, percentage });
    }
  }, []);

  // Función para cargar datos
  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Cargar datos básicos en paralelo
      const [inventoryData, productsData, ordersData, statsData] = await Promise.all([
        fetchInventoryOptimized(forceRefresh),
        fetchProductsOptimized(forceRefresh),
        fetchPendingOrdersOptimized(),
        getInventoryStats()
      ]);

      setInventory(inventoryData || []);
      setProducts(productsData || []);
      setOrders(ordersData || []);
      setStats(statsData);

      // Las imágenes ya están incluidas en productsData, no necesitamos cargarlas por separado
      setProductImages({});

      // Debug dirigido por localStorage o valor por defecto (UUID del caso)
      try {
        const debugId = (typeof localStorage !== 'undefined' && localStorage.getItem('inventoryDebugId')) || 'a5006d2b-8a31-444b-8c13-99fa2798ce08';
        if (debugId) {
          const inv = (inventoryData || []).filter(i => String(i.product_id) === String(debugId));
          const pendingSum = inv.reduce((s, it) => s + (Number(it.pending_orders) || 0), 0);
          const qtySum = inv.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
          const prod = (productsData || []).find(p => String(p.id) === String(debugId));
          // eslint-disable-next-line no-console
          console.log('🔎 INV-DEBUG fetchData', {
            debugId,
            productCode: prod?.code,
            rows: inv.length,
            qtySum,
            pendingSum,
            sample: inv.slice(0, 8)
          });
        }
      } catch (_) {}

    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Función para recálculo
  const recalculate = useCallback(async () => {
    try {
      setRecalculating(true);
      setError(null);
      setProgress({ message: "Iniciando recálculo...", percentage: 0 });

      const result = await recalculateInventoryOptimized(handleProgress);
      
      // Recargar datos con caché limpio
      await fetchData(true);
      
      return result;
    } catch (err) {
      console.error('Error recalculating inventory:', err);
      setError(err.message);
      throw err;
    } finally {
      setRecalculating(false);
      setProgress({ message: "", percentage: 0 });
    }
  }, [fetchData, handleProgress]);

  // Función para refrescar datos
  const refresh = useCallback(async () => {
    clearInventoryCache();
    await fetchData(true);
  }, [fetchData]);

  // Memoizar inventario visible
  const visibleInventory = useMemo(() => {
    return inventory.filter(item => {
      const quantity = parseInt(item.quantity);
      const pending = parseInt(item.pending_orders);
      return (Number.isFinite(quantity) && quantity !== 0) || (Number.isFinite(pending) && pending !== 0);
    });
  }, [inventory]);

  // Memorizar productos que necesitan fabricación
  const productsNeedingManufacturing = useMemo(() => {
    return products.filter(product => {
      const productInventory = inventory.filter(item => item.product_id === product.id);
      
      // Verificar si hay pedidos pendientes
      const hasPendingOrders = productInventory.some(item => item.pending_orders > 0);
      
      // Verificar si hay carencias específicas (pendientes > stock) en alguna combinación color/talla
      const hasSpecificShortages = productInventory.some(item => {
        const stock = item.quantity || 0;
        const pending = item.pending_orders || 0;
        return pending > stock;
      });
      
      return hasPendingOrders && hasSpecificShortages;
    });
  }, [products, inventory]);

  // Estadísticas derivadas localmente desde el inventario completo para evitar límites del API
  const derivedStats = useMemo(() => {
    try {
      const totals = (inventory || []).reduce((acc, item) => {
        const quantity = Number(item.quantity) || 0;
        const pending = Number(item.pending_orders) || 0;
        acc.totalStock += quantity;
        acc.totalPending += pending;
        // Stock utilizable para servir pedidos: lo que puede cubrir inmediatamente
        acc.totalUsable += Math.min(quantity, pending);
        // Stock libre: que no está comprometido por pedidos
        acc.totalFree += Math.max(quantity - pending, 0);
        if (quantity > 0) acc.itemsWithStock += 1;
        if (pending > 0) acc.itemsWithPending += 1;
        return acc;
      }, { totalStock: 0, totalPending: 0, itemsWithStock: 0, itemsWithPending: 0, totalValue: 0, totalUsable: 0, totalFree: 0 });

      // Calcular valor total usando precios de products
      const priceById = new Map((products || []).map(p => [p.id, Number(p.price) || 0]));
      let totalValue = 0;
      for (const item of (inventory || [])) {
        const qty = Number(item.quantity) || 0;
        const price = priceById.get(item.product_id) || 0;
        totalValue += qty * price;
      }
      totals.totalValue = totalValue;
      return totals;
    } catch (_) {
      return { totalStock: 0, totalPending: 0, totalValue: 0, itemsWithStock: 0, itemsWithPending: 0, totalUsable: 0, totalFree: 0 };
    }
  }, [inventory, products]);

  // Memorizar mapa de inventario para acceso rápido
  const inventoryMap = useMemo(() => {
    const map = {};
    inventory.forEach(item => {
      const key = `${item.product_id}::${item.size}::${item.color}`;
      map[key] = item;
    });
    return map;
  }, [inventory]);

  // Memorizar productos agrupados por inventario
  const productsWithInventory = useMemo(() => {
    return products.map(product => ({
      ...product,
      inventory: inventory.filter(item => item.product_id === product.id)
    }));
  }, [products, inventory]);

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Suscribirse a cambios en la tabla inventory para refrescar automáticamente
  useEffect(() => {
    try {
      const channel = supabase
        .channel('inventory-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory' }, () => {
          // Limpiar caché y refrescar datos cuando haya cambios
          clearInventoryCache();
          fetchData(true);
        })
        .subscribe();

      return () => {
        try { supabase.removeChannel(channel); } catch (_) {}
      };
    } catch (_) { return () => {}; }
  }, [fetchData]);

  return {
    // Estado
    inventory,
    products,
    productImages,
    orders,
    loading,
    recalculating,
    progress,
    stats: derivedStats,
    error,
    
    // Datos procesados
    visibleInventory,
    productsNeedingManufacturing,
    inventoryMap,
    productsWithInventory,
    
    // Acciones
    fetchData,
    recalculate,
    refresh,
    clearCache: clearInventoryCache
  };
}; 